import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import Contact from '../models/Contact';
import { ContactOption } from '../models/enums/ContactOption';
import { addNewContact } from '../services/contact-service';
import ApiResponse from '../models/ApiResponse';
import {useAuth} from "../context/AuthContext";

interface AddContactModalProps {
    isOpen: boolean;
    onClose: () => void;
    onError: (message: string) => void;
    onAddContact: (contact: Contact) => void;
    clientId: number;
}

const AddContactModal: React.FC<AddContactModalProps> = ({
                                                             isOpen,
                                                             onClose,
                                                             onError,
                                                             onAddContact,
                                                             clientId
                                                         }) => {
    const maxNotes = 1000;

    const contactOptions = Object.values(ContactOption).map(method => ({
        value: method,
        label: method,
    }));

    const initialForm = {
        name: '',
        email: '',
        phone: '',
        notes: '',
        preferredContact: contactOptions[0].value,
    };

    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const {user} = useAuth();

    useEffect(() => {
        if (!isOpen) {
            setForm(initialForm);
            setErrors({});
        }
    }, [isOpen]);

    const validate = () => {
        const errs: Record<string, string> = {};
        if (!form.name.trim()) errs.name = 'Name is required';
        if (form.name.length > 100) errs.name = 'Name must be 100 characters or fewer';
        if (form.email && !/.+@.+\..+/.test(form.email)) errs.email = 'Invalid email';
        if (form.email && form.email.length > 100) errs.email = 'Email must be 100 characters or fewer';
        if (form.phone && !/^\+?[0-9]{7,15}$/.test(form.phone)) errs.phone = 'Invalid phone number (do not use dashes)';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        setLoading(true);
        if (!validate()) {
            setLoading(false);
            return;
        }

        const payload: Contact = {
            name: form.name.trim(),
            preferredContact: form.preferredContact,
            email: form.email.trim() || undefined,
            phone: form.phone.trim() || undefined,
            notes: form.notes.trim() || undefined,
            userId: user ? user.id : undefined,
            clientId: clientId
        };

        try {
            const response: ApiResponse<Contact> = await addNewContact(payload);
            if (response.status === 201 && response.data) {
                onAddContact(response.data);
            } else if (response.status === 403) {
                onError('Contact limit has been reached under your plan. You can upgrade to premium to create more contacts.');
            } else {
                onError('Unable to add contact. Please try again later');
            }
        } catch (e) {
            onError('Unable to add contact. Please try again later');
        } finally {
            setLoading(false);
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50 flex items-center justify-center">
            <div
                className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm"
                aria-hidden="true"
                onClick={onClose}
            />

            <div className="relative bg-white w-full max-w-md p-6 rounded-lg shadow-lg overflow-auto max-h-screen">
                <Dialog.Title className="text-xl font-semibold mb-4">Add New Contact</Dialog.Title>

                <div className="space-y-4">
                    <div>
                        <label htmlFor="contact-name" className="block font-medium">
                            Name *
                        </label>
                        <input
                            id="contact-name"
                            type="text"
                            value={form.name}
                            onChange={e => setForm({ ...form, name: e.target.value })}
                            className="w-full border rounded p-2"
                        />
                        {errors.name && <p data-testid="name-error" className="text-red-600 text-sm">{errors.name}</p>}
                    </div>

                    <div>
                        <label htmlFor="contact-email" className="block font-medium">
                            Email
                        </label>
                        <input
                            id="contact-email"
                            type="email"
                            value={form.email}
                            onChange={e => setForm({ ...form, email: e.target.value })}
                            className="w-full border rounded p-2"
                        />
                        {errors.email && <p data-testid="email-error" className="text-red-600 text-sm">{errors.email}</p>}
                    </div>

                    <div>
                        <label htmlFor="contact-phone" className="block font-medium">
                            Phone
                        </label>
                        <input
                            id="contact-phone"
                            type="tel"
                            value={form.phone}
                            onChange={e => setForm({ ...form, phone: e.target.value })}
                            className="w-full border rounded p-2"
                        />
                        {errors.phone && <p className="text-red-600 text-sm">{errors.phone}</p>}
                    </div>

                    <div>
                        <label htmlFor="contact-notes" className="block font-medium">
                            Notes
                        </label>
                        <textarea
                            id="contact-notes"
                            rows={3}
                            maxLength={maxNotes}
                            value={form.notes}
                            onChange={e => setForm({ ...form, notes: e.target.value })}
                            className="w-full border rounded p-2"
                        />
                        <p className="text-gray-500 text-sm mt-1">
                            {form.notes.length}/{maxNotes} characters
                        </p>
                    </div>

                    <div>
                        <label htmlFor="preferred-contact" className="block font-medium">Preferred Contact *</label>
                        <select
                            id="preferred-contact"
                            className="w-full border px-3 py-2 rounded"
                            value={form.preferredContact}
                            onChange={e => setForm({...form, preferredContact: e.target.value as ContactOption})}
                        >
                            {contactOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex justify-end space-x-3 mt-6">
                        <button onClick={onClose} className="px-4 py-2 rounded border">
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            className="px-4 py-2 rounded bg-crumbblue text-white"
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default AddContactModal;
